import { useEffect, useMemo, useState } from "react";
import "./AdminHandleActivities.scss";
import { MultiSelect } from "../../components/UI/MultiSelect/MultiSelect";
import { IActivity, ICourt, IPermission } from "../../types/api";
import { combinedQuery } from "../../api/combinedQueries/combinedQueries";
import Spinner from "../../components/UI/Spinner/Spinner";
import { CognitoUser } from "../../types/cognito";
import { Auth } from "aws-amplify";
import EditActivityPopup from "../../containers/Activities/EditActivityPopup/EditActivityPopup";
import { deleteActivityTemplate } from "../../api/activities/activities";
import HandleConfirmationPopup from "../../containers/HandleConfirmation/HandleConfirmationPopup";
import { toast } from "react-toastify";

interface IAdminHandleActivitiesProps {}

function AdminHandleActivities(props: IAdminHandleActivitiesProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [activities, setActivities] = useState<IActivity[]>([]);
  const [
    showHandleDeleteActivityTemplatePopup,
    setShowHandleDeleteActivityTemplatePopup,
  ] = useState<boolean>(false);
  const [activityToEdit, setActivityToEdit] = useState<IActivity>();
  const [activityTemplates, setActivityTemplates] = useState<IActivity[]>([]);
  const [activityTemplateToDelete, setActivityTemplateToDelete] =
    useState<IActivity>();
  const [courts, setCourts] = useState<ICourt[]>([]);
  const [showEditAcitivityPopup, setShowEditActivityPopup] =
    useState<boolean>(false);
  const [currentActivityType, setCurrentActivityType] = useState<
    "ACTIVITIES" | "ACTIVITY_TEMPLATES"
  >("ACTIVITIES");
  const [filteredActivities, setFilteredActivities] = useState<IActivity[]>([]);
  const [sortColumn, setSortColumn] = useState<string>("datetime_start");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [searchString, setSearchString] = useState<string>("");
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [selectedCourtIds, setSelectedCourtIds] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const sortedActivities = [...activities].sort((a, b) => {
    let comparison = 0;

    if (sortColumn === "name") {
      comparison = a.name.localeCompare(b.name);
    } else if (sortColumn === "datetime_start") {
      const dateA = new Date(a.datetime_start).getTime();
      const dateB = new Date(b.datetime_start).getTime();
      comparison = dateA - dateB;
    }

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const [permissions, setPermissions] = useState<IPermission[]>([]);
  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((userData) => {
        setUser(userData);
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  }, []);

  const fetchChosenItems = async ({
    fetchUpcomingActivities = false,
    fetchCourts = false,
    fetchPermissions = false,
    fetchActivities = false,
  }) => {
    if (!user) return;
    setLoading(true);

    try {
      const response = await combinedQuery({
        requestor: "admin",
        fetchUpcomingActivities: fetchUpcomingActivities,
        fetchCourts: fetchCourts,
        fetchPermissions: fetchPermissions,
        fetchActivities: fetchActivities,
      });
      if (
        response.upcomingActivities &&
        !("message" in response.upcomingActivities)
      ) {
        setActivities(response.upcomingActivities);
      }
      if (response.courts && !("message" in response.courts)) {
        setCourts(response.courts);
      }
      if (response.permissions && !("message" in response.permissions)) {
        setPermissions(response.permissions);
      }
      if (response.activities && !("message" in response.activities)) {
        setActivityTemplates(response.activities);
      }

      setLoading(false);
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  };
  // The sorted column's direction, as a chevron rather than a triangle glyph -
  // the triangle rendered heavier than anything else in the header row.
  const sortIcon = (column: string) =>
    sortColumn === column ? (
      <i
        className={`fa-regular fa-chevron-${
          sortOrder === "asc" ? "up" : "down"
        } sort-indicator`}
      />
    ) : null;

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };
  const handleSearchActivities = () => {
    const filtered = activities.filter((activity) =>
      activity.name
        .split("%-%")[0]
        .toLowerCase()
        .includes(searchString.toLowerCase())
    );
    setFilteredActivities(filtered);
  };
  const handleToggleActivityType = (
    type: "ACTIVITIES" | "ACTIVITY_TEMPLATES"
  ) => {
    setCurrentActivityType(type);
    setSearchString("");
  };

  const handleDeleteAcvitityTemplate = (name: string) => async () => {
    try {
      const response = await deleteActivityTemplate(name);
      if (!("message" in response)) {
        toast.success("Aktivitetsmallen raderades");
        setActivityTemplateToDelete(undefined);
        fetchChosenItems({
          fetchActivities: true,
        });
      }
    } catch (error) {
      console.error("Error deleting activity template:", error);
    }
  };
  useEffect(() => {
    handleSearchActivities();
  }, [searchString]);
  useEffect(() => {
    fetchChosenItems({
      fetchUpcomingActivities: true,
      fetchCourts: true,
      fetchPermissions: true,
      fetchActivities: true,
    });
  }, [user]);
  useEffect(() => {
    if (activityTemplateToDelete) {
      setShowHandleDeleteActivityTemplatePopup(true);
    }
  }, [activityTemplateToDelete]);
  const openEditActivity = (activity: IActivity) => {
    setShowEditActivityPopup(true);
    setActivityToEdit(activity);
  };
  // Occurrences carry a technical suffix after %-%; the part before it is the
  // activity as an admin thinks of it, and what the picker lists.
  const baseName = (name: string) => name.split("%-%")[0];

  const activityNameOptions = useMemo(
    () =>
      Array.from(new Set(activities.map((activity) => baseName(activity.name))))
        .sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" }))
        .map((name) => ({ label: name, value: name })),
    [activities],
  );

  // Only courts that actually host an upcoming activity, so the picker does not
  // offer twenty banor where three are in use.
  const courtOptions = useMemo(() => {
    const used = new Set(
      activities.flatMap((activity) => activity.court_id ?? []),
    );
    return courts
      .filter((court) => used.has(court.court_id))
      .map((court) => ({ label: court.name, value: court.court_id }));
  }, [activities, courts]);

  // The four colours an activity can be given, named as they look. Only the
  // ones actually in use are offered.
  const colorOptions = useMemo(() => {
    const labels: Record<string, string> = {
      "activity-color-1": "Ljusblå",
      "activity-color-2": "Orange",
      "activity-color-3": "Gul",
      "activity-color-4": "Lila",
    };
    const used = new Set(activities.map((activity) => activity.color));
    return Object.entries(labels)
      .filter(([value]) => used.has(value))
      .map(([value, label]) => ({
        value,
        label,
        icon: <span className={`activity-color-blob ${value}`} />,
      }));
  }, [activities]);

  // Inne and Ute from the court's own type rather than a name range, so a new
  // court lands in the right group without anyone editing a list.
  const courtPresets = useMemo(() => {
    const idsOfType = (type: string) =>
      courts
        .filter(
          (court) =>
            court.court_type === type &&
            courtOptions.some((option) => option.value === court.court_id),
        )
        .map((court) => court.court_id);
    return [
      { label: "Inne", values: idsOfType("INDOOR") },
      { label: "Ute", values: idsOfType("OUTDOOR") },
    ].filter((preset) => preset.values.length > 0);
  }, [courts, courtOptions]);

  const hasActiveFilters =
    selectedNames.length > 0 ||
    selectedCourtIds.length > 0 ||
    selectedColors.length > 0 ||
    searchString.length > 0;

  const resetFilters = () => {
    setSelectedNames([]);
    setSelectedCourtIds([]);
    setSelectedColors([]);
    setSearchString("");
  };

  if (loading) return <Spinner />;

  const activitiesToLoop = (
    searchString.length > 0 ? filteredActivities : sortedActivities
  ).filter((activity) => {
    if (
      selectedNames.length > 0 &&
      !selectedNames.includes(baseName(activity.name))
    ) {
      return false;
    }
    if (
      selectedCourtIds.length > 0 &&
      !(activity.court_id ?? []).some((id) => selectedCourtIds.includes(id))
    ) {
      return false;
    }
    if (selectedColors.length > 0 && !selectedColors.includes(activity.color)) {
      return false;
    }
    return true;
  });
  return (
    <div className="admin-activities-page-container">
      <div className="admin-activities-layout">
        <div className="admin-activities-declarations">
          <h1 className="text-l primary-text">Aktiviteter</h1>
        </div>
        <div className="handle-activities-actions-wrapper">
          <div className="admin-handle-activity-toggle-wrapper text-m">
            <button
              className={`toggle-button ${
                currentActivityType === "ACTIVITIES" ? "active" : ""
              }`}
              onClick={() => handleToggleActivityType("ACTIVITIES")}
            >
              Aktiviteter
            </button>
            <button
              className={`toggle-button ${
                currentActivityType === "ACTIVITY_TEMPLATES" ? "active" : ""
              }`}
              onClick={() => handleToggleActivityType("ACTIVITY_TEMPLATES")}
            >
              Mallar
            </button>
          </div>
          <div className="activities-search-wrapper">
            <div className="search-input-wrapper">
              <input
                className="activities-search-input text-m"
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                placeholder="Sök "
              ></input>
              <i className="fa-regular fa-magnifying-glass search-icon"></i>
            </div>
          </div>
        </div>
        {currentActivityType === "ACTIVITIES" && (
          <div className="activities-filter-wrapper text-m">
            <span className="secondary-text">Filtreringar:</span>
            <div className="activities-filter-item">
              <MultiSelect
                label="Aktiviteter"
                options={activityNameOptions}
                selected={selectedNames}
                multiple
                onChange={(value) => setSelectedNames(value as string[])}
                formatSelected={(value) =>
                  `Aktiviteter (${(value as string[]).length})`
                }
                searchable
              />
            </div>
            <div className="activities-filter-item">
              <MultiSelect
                label="Banor"
                options={courtOptions}
                selected={selectedCourtIds}
                multiple
                onChange={(value) => setSelectedCourtIds(value as string[])}
                formatSelected={(value) =>
                  `Banor (${(value as string[]).length})`
                }
                presetOptions={courtPresets}
              />
            </div>
            <div className="activities-filter-item">
              <MultiSelect
                label="Färg"
                options={colorOptions}
                selected={selectedColors}
                multiple
                onChange={(value) => setSelectedColors(value as string[])}
                formatSelected={(value) =>
                  `Färg (${(value as string[]).length})`
                }
              />
            </div>
            <div className="activities-filter-item">
              <button
                type="button"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
              >
                Återställ filter
              </button>
            </div>
          </div>
        )}
        {currentActivityType === "ACTIVITY_TEMPLATES" && (
          <table className="activities-table activities-table-templates">
            <thead>
              <tr>
                <th
                  className="text-m secondary-text pointer"
                  onClick={() => handleSort("name")}
                >
                  Namn {sortIcon("name")}
                </th>
                <th className="text-m secondary-text">Banor</th>
                <th className="text-m secondary-text">Synlighet</th>
                <th className="text-m secondary-text">Färg</th>
              </tr>
            </thead>
            <tbody>
              {activityTemplates.map((activity, index) => {
                return (
                  <tr key={index}>
                    <td className="text-m primary-text" onClick={() => null}>
                      {activity.name.split("%-%")[0]}
                    </td>

                    <td className="text-m primary-text" onClick={() => null}>
                      {activity.court_id.map((court) => {
                        const courtName = courts.find(
                          (courtItem) => courtItem.court_id === court,
                        )?.name;

                        return courtName + " ";
                      })}
                    </td>
                    <td className="text-m primary-text" onClick={() => null}>
                      {activity.hidden ? "Dold" : "Synlig"}
                    </td>
                    <td className="text-m primary-text" onClick={() => null}>
                      <div
                        className={`activity-color-blob ${activity.color}`}
                      ></div>
                      <div
                        onClick={() => setActivityTemplateToDelete(activity)}
                      >
                        <i className="fa-regular fa-trash"></i>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {currentActivityType === "ACTIVITIES" && (
          <table className="activities-table activities-table-occurrences">
            <thead>
              <tr>
                <th
                  className="text-m secondary-text pointer"
                  onClick={() => handleSort("name")}
                >
                  Namn {sortIcon("name")}
                </th>
                <th
                  className="text-m secondary-text pointer"
                  onClick={() => handleSort("datetime_start")}
                >
                  Start {sortIcon("datetime_start")}
                </th>
                <th className="text-m secondary-text">Slut</th>
                <th className="text-m secondary-text">Banor</th>
                <th className="text-m secondary-text">Synlighet</th>
                <th className="text-m secondary-text">Färg</th>
              </tr>
            </thead>
            <tbody>
              {activitiesToLoop.map((activity, index) => {
                const startDate =
                  activity.datetime_start.slice(8, 10) +
                  " / " +
                  activity.datetime_start.slice(5, 7) +
                  " - " +
                  activity.datetime_start.slice(11, 16);

                const endDate =
                  activity.datetime_end.slice(8, 10) +
                  " / " +
                  activity.datetime_end.slice(5, 7) +
                  " - " +
                  activity.datetime_end.slice(11, 16);

                return (
                  <tr key={index}>
                    <td className="text-m primary-text" onClick={() => null}>
                      {activity.name.split("%-%")[0]}
                    </td>
                    <td className="text-m primary-text" onClick={() => null}>
                      {startDate}
                    </td>
                    <td className="text-m primary-text" onClick={() => null}>
                      {endDate}
                    </td>
                    <td className="text-m primary-text" onClick={() => null}>
                      {activity.court_id.map((court) => {
                        const courtName = courts.find(
                          (courtItem) => courtItem.court_id === court,
                        )?.name;

                        return courtName + " ";
                      })}
                    </td>
                    <td className="text-m primary-text" onClick={() => null}>
                      {activity.hidden ? "Dold" : "Synlig"}
                    </td>
                    <td className="text-m primary-text" onClick={() => null}>
                      <div
                        className={`activity-color-blob ${activity.color}`}
                      ></div>
                      <div
                        className="edit-component"
                        onClick={() => openEditActivity(activity)}
                      >
                        <div className="edit-dot"></div>
                        <div className="edit-dot"></div>
                        <div className="edit-dot"></div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <HandleConfirmationPopup
        showPopup={showHandleDeleteActivityTemplatePopup}
        onClose={() => setShowHandleDeleteActivityTemplatePopup(false)}
        onConfirm={() => {
          activityTemplateToDelete &&
            handleDeleteAcvitityTemplate(activityTemplateToDelete.name)();
          setShowHandleDeleteActivityTemplatePopup(false);
        }}
        questionText="Är du säker på att du vill radera aktivitetsmallen?"
        confirmText="Ja"
      />

      <EditActivityPopup
        showPopup={showEditAcitivityPopup}
        onClose={() => setShowEditActivityPopup(false)}
        activity={activityToEdit}
        onConfirm={() => fetchChosenItems({ fetchUpcomingActivities: true })}
        courts={courts}
        permissions={permissions}
        coachesList={[]}
      />
    </div>
  );
}

export default AdminHandleActivities;
